import AwsMarketplaceRegistrationDialog from "components/AwsMarketplaceRegistrationDialog";
import { Button } from "components/ui/button";
import TierZeroIcon from "components/ui/icons/tierzero_mark";
import { CheckCircle, Loader2,XCircle } from "lucide-react";
import React, { useEffect, useState } from "react";
import { getBrandThemeFromDomain, getProductName, getSupportEmail } from "src/utils/brandTheme";

function AwsMarketplacePage() {
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRegistrationDialogOpen, setIsRegistrationDialogOpen] = useState(false);

  useEffect(() => {
    // Parse URL parameters manually since we're not using React Router
    const urlParams = new URLSearchParams(window.location.search);
    const statusParam = urlParams.get("status");
    const customerIdParam = urlParams.get("customer_id");
    const errorParam = urlParams.get("error");
    const messageParam = urlParams.get("message");

    if (statusParam === "success" && customerIdParam) {
      setStatus("success");
      setCustomerId(customerIdParam);
    } else if (errorParam) {
      setStatus("error");
      setErrorMessage(messageParam || getErrorMessage(errorParam));
    } else {
      // Still loading or processing
      setStatus("loading");
    }
  }, []);

  const getErrorMessage = (error: string) => {
    switch (error) {
      case "missing_token":
        return "Missing registration token. Please try subscribing again from AWS Marketplace.";
      case "registration_failed":
        return "We encountered an error setting up your account. Please try again.";
      default:
        return "An unexpected error occurred during registration.";
    }
  };

  const handleGetStarted = () => {
    setIsRegistrationDialogOpen(true);
  };


  const brandTheme = getBrandThemeFromDomain();
  const supportEmail = getSupportEmail(brandTheme);
  const productName = getProductName(brandTheme);

  const handleContactSupport = () => {
    window.location.href = `mailto:${supportEmail}?subject=AWS Marketplace Registration Issue`;
  };

  const handleRetrySubscription = () => {
    // Redirect back to AWS Marketplace
    window.open("https://aws.amazon.com/marketplace/pp/prodview-your-product-id", "_blank");
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-midnight to-dark-teal text-white flex items-center justify-center p-4">
        <div className="bg-steel/30 backdrop-blur-sm rounded-xl p-8 shadow-xl border border-steel/50 w-full max-w-md text-center">
          <div className="mx-auto mb-4">
            <Loader2 className="h-12 w-12 animate-spin text-cyan" />
          </div>
          <h2 className="text-xl font-medium text-white mb-3">Setting up your account...</h2>
          <p className="text-slate-300">
            Please wait while we process your AWS Marketplace subscription.
          </p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-midnight to-dark-teal text-white flex items-center justify-center p-4">
        <div className="bg-steel/30 backdrop-blur-sm rounded-xl p-8 shadow-xl border border-steel/50 w-full max-w-md text-center">
          <div className="mx-auto mb-4">
            <XCircle className="h-12 w-12 text-red-400" />
          </div>
          <h2 className="text-xl font-medium text-white mb-3">Registration Error</h2>
          <p className="text-slate-300 mb-6">
            {errorMessage}
          </p>
          <div className="space-y-4">
            <Button 
              onClick={handleContactSupport}
              className="w-full bg-slate-600 hover:bg-slate-700 text-white border-slate-500"
              variant="outline"
            >
              Contact Support
            </Button>
            <Button 
              onClick={handleRetrySubscription}
              className="w-full bg-gradient-to-r from-cyan to-[#56A64B] hover:from-[#56A64B] hover:to-cyan text-white font-medium"
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gradient-to-b from-midnight to-dark-teal text-white flex items-center justify-center p-4">
      <div className="max-w-2xl mx-auto text-center">
        <div className="mb-8">
          <div className="mx-auto mb-6">
            <TierZeroIcon className="h-16 w-auto mx-auto text-white" />
          </div>
          <h1 className="text-4xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-cyan to-[#56A64B]">
            Welcome to {productName}!
          </h1>
          <p className="text-xl text-slate-300 flex items-center justify-center gap-2">
            <CheckCircle className="h-6 w-6 text-[#56A64B]" />
            Your AWS Marketplace subscription has been successfully activated.
          </p>
        </div>

        <div className="bg-steel/30 backdrop-blur-sm rounded-xl p-8 shadow-xl border border-steel/50 space-y-6">
          <div className="bg-dark-steel/50 p-6 rounded-lg border border-steel-border/50">
            <h3 className="font-semibold text-cyan mb-3">What's Next?</h3>
            <ul className="text-sm text-slate-300 space-y-2 text-left">
              <li>• Connect your monitoring tools and data sources</li>
              <li>• Set up your first investigation workflows</li>
              <li>• Explore AI-powered incident analysis</li>
              <li>• Configure team notifications and alerts</li>
            </ul>
          </div>
          
          {customerId && (
            <div className="bg-dark-steel/30 p-4 rounded-lg border border-steel-border/30">
              <span className="font-medium text-slate-300">Customer ID:</span> 
              <span className="text-cyan ml-2">{customerId}</span>
            </div>
          )}
          
          <Button 
            onClick={handleGetStarted}
            className="w-full bg-gradient-to-r from-cyan to-[#56A64B] hover:from-[#56A64B] hover:to-cyan text-white font-medium px-8 py-3 text-lg"
            size="lg"
          >
            Get Started
          </Button>
          
          <div className="text-center text-sm text-slate-400">
            Need help? <button 
              onClick={handleContactSupport}
              className="text-cyan hover:text-[#56A64B] transition-colors"
            >
              Contact our support team
            </button>
          </div>
        </div>
      </div>

      {/* Registration Dialog */}
      {customerId && (
        <AwsMarketplaceRegistrationDialog
          isOpen={isRegistrationDialogOpen}
          onClose={() => setIsRegistrationDialogOpen(false)}
          customerId={customerId}
        />
      )}
    </div>
  );
}

export default AwsMarketplacePage;